import { Grid } from '@mui/material';
import { CustomFields as CustomFieldsValueObject } from '@nx-smartmonkey/shared/domain';
import {
  HSpacer,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  RoutalTooltip,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { Check } from '@mui/icons-material';
import Constraints from '../../../../assets/svg/right-panel-tab-icons/ConstraintsIcon';
import InformationIcon from '../../../../assets/svg/right-panel-tab-icons/InformationIcon';
import { CustomFields } from '../../../../components/custom-fields/CustomFields';
import {
  InputLocations,
  renderArray,
  RenderInputPhone,
  renderInputText,
  renderTimewindows,
} from '../../../../components/right-panel-info/Common';
import { InfoContainer } from '../../../../components/right-panel-info/RightPanel.styled';
import { Project } from '../../../../domain/project/Project';
import { FuelTypes, Vehicle, WeekDay, weekDays } from '../../../../domain/vehicle/Vehicle';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { consumptionByFuelType, kmToSM, SMToKm } from '../../../../helpers/Units';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import { useUpdateVehicle } from '../../../../hooks/vehicle/useUpdateVehicle';
import { GeoFencesSelector } from '../../../../react-components/GeoFencesSelector';
import Text from '../../../../react-components/Text';

interface VehicleInfoSectionProps {
  vehicle: Vehicle;
  section: number;
  project: Project;
  handleUpdate: (label: string, val: any) => void;
}

const VehicleInfoSection = ({ vehicle, section, project, handleUpdate }: VehicleInfoSectionProps) => {
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();

  if (!project) {
    return null;
  }

  const vehicleCustomFieldDefinitions = (project.custom_fields?.vehicle ?? []).filter((cf) =>
    project.planner.vehicleHasCustomField(cf.custom_id)
  );

  switch (section) {
    case 0:
      return (
        <div style={{ height: `100%`, overflowY: `auto`, minHeight: `300px` }}>
          <InfoContainer>
            <Text
              variant="h6"
              style={{ cursor: `default`, fontWeight: 600 }}
              intlId="clients.details.group.information"
            />

            <VSpacer medium />

            {project.planner.vehicleFieldsHasSpecificField(`plate`) && (
              <>
                <Text
                  intlId="vehicles.data.details.plate"
                  variant="label"
                  style={{ cursor: `default`, marginBottom: `8px` }}
                />
                {renderInputText({
                  placeholderIntlId: `vehicles.data.details.plate`,
                  variant: `outlined`,
                  property: `plate`,
                  handleUpdate,
                  data: vehicle,
                })}
                <VSpacer medium />
              </>
            )}

            <Text
              intlId="vehicles.data.details.vehicle_model"
              variant="label"
              style={{ cursor: `default`, marginBottom: `8px` }}
            />
            {renderInputText({
              placeholderIntlId: `vehicles.data.details.vehicle_model`,
              variant: `outlined`,
              property: `vehicle_model`,
              handleUpdate,
              data: vehicle,
            })}
            <VSpacer medium />

            <Text
              intlId="vehicles.data.details.brand"
              variant="label"
              style={{ cursor: `default`, marginBottom: `8px` }}
            />
            {renderInputText({
              placeholderIntlId: `vehicles.data.details.brand`,
              variant: `outlined`,
              property: `brand`,
              handleUpdate,
              data: vehicle,
            })}
            <VSpacer medium />

            {project.planner.vehicleFieldsHasSpecificField(`price_per_minute`) && (
              <>
                <Text
                  intlId="vehicles.data.details.price_per_minute"
                  variant="label"
                  style={{ cursor: `default`, marginBottom: `8px` }}
                />
                {renderInputText({
                  variant: `outlined`,
                  property: `price_per_minute`,
                  handleUpdate,
                  data: vehicle,
                  placeholderIntlId: `price_per_minute`,
                  placeholderIntlValues: { currency: project?.units?.currencyToSymbol() || `` },
                  suffix: intl.formatMessage(
                    { id: `price_per_minute` },
                    { currency: project?.units?.currencyToSymbol() }
                  ),
                })}
                <VSpacer medium />
              </>
            )}

            {project.planner.vehicleFieldsHasSpecificField(`price_per_distance`) && (
              <>
                <Text
                  intlId="vehicles.data.details.price_per_distance"
                  variant="label"
                  style={{ cursor: `default`, marginBottom: `8px` }}
                />
                {renderInputText({
                  variant: `outlined`,
                  property: `price_per_distance`,
                  handleUpdate,
                  data: vehicle,
                  placeholderIntlId: `price_per_distance`,
                  placeholderIntlValues: {
                    currency: project?.units?.currencyToSymbol() || ``,
                    unit: project?.units?.distance || ``,
                  },
                  suffix: intl.formatMessage(
                    { id: `price_per_distance` },
                    {
                      currency: project?.units?.currencyToSymbol(),
                      unit: project?.units?.distance,
                    }
                  ),
                })}
                <VSpacer medium />
              </>
            )}

            <Text
              intlId="vehicles.data.details.fuel_type"
              variant="label"
              style={{ cursor: `default`, marginBottom: `8px` }}
            />
            <RoutalSelect
              id="fuel_type"
              style={{
                height: `36px`,
                margin: 0,
              }}
              popoverProps={{
                options: [
                  ...([`gasoline`, `diesel`, `electric`] as FuelTypes[]).map(
                    (fuelType, index): RoutalSelectMultiSelectOption => ({
                      key: fuelType,
                      onClick: () => {
                        handleUpdate(`fuel_type`, fuelType);
                      },
                      height: 55,
                      content: (
                        <Grid
                          container
                          direction="row"
                          wrap="nowrap"
                          alignItems="center"
                          style={{ overflow: `hidden` }}
                        >
                          <Text
                            variant="label"
                            style={{ color: RoutalPalette.text }}
                            intlId={`vehicle.fuel_type.${fuelType}`}
                          />
                        </Grid>
                      ),
                    })
                  ),
                  ...[
                    vehicle.fuel_type
                      ? ({
                          key: `none`,
                          onClick: () => {
                            handleUpdate(`fuel_type`, null);
                          },
                          height: 55,
                          content: (
                            <Grid
                              container
                              direction="row"
                              wrap="nowrap"
                              alignItems="center"
                              style={{ overflow: `hidden` }}
                            >
                              <Text variant="label" style={{ color: RoutalPalette.text }} intlId={`none`} />
                            </Grid>
                          ),
                        } as RoutalSelectMultiSelectOption)
                      : null,
                  ],
                ],
              }}
            >
              <Grid container direction="row" wrap="nowrap" style={{ width: `100%` }} alignItems="center">
                {vehicle.fuel_type ? (
                  <Text
                    variant="label"
                    style={{ color: RoutalPalette.text }}
                    intlId={`vehicle.fuel_type.${vehicle.fuel_type}`}
                  />
                ) : (
                  <Text variant="label" intlId={`vehicles.data.details.fuel_type`} />
                )}
              </Grid>
            </RoutalSelect>
            <VSpacer medium />

            {vehicle.fuel_type && (
              <>
                <Text
                  intlId="vehicles.data.details.consumption"
                  variant="label"
                  style={{ cursor: `default`, marginBottom: `8px` }}
                />
                {renderInputText({
                  variant: `outlined`,
                  property: `consumption`,
                  handleUpdate,
                  data: vehicle,
                  placeholder: consumptionByFuelType(vehicle.fuel_type, project?.units?.distance ?? `km`),
                  suffix: consumptionByFuelType(vehicle.fuel_type, project?.units?.distance ?? `km`),
                })}
                <VSpacer medium />
              </>
            )}
          </InfoContainer>

          <InfoContainer>
            <Text variant="h6" style={{ cursor: `default`, fontWeight: 600 }} intlId="clients.details.group.contact" />

            <VSpacer medium />

            {project.planner.vehicleFieldsHasSpecificField(`email`) && (
              <>
                {renderInputText({
                  labelIntlId: `vehicles.data.details.email`,
                  variant: `outlined`,
                  property: `email`,
                  type: `email`,
                  handleUpdate,
                  data: vehicle,
                })}
                <VSpacer medium />
              </>
            )}
            {project.planner.vehicleFieldsHasSpecificField(`comments`) && (
              <>
                {renderInputText({
                  labelIntlId: `vehicles.data.details.comments`,
                  variant: `outlined`,
                  property: `comments`,
                  multiline: true,
                  rowsMin: 4,
                  handleUpdate,
                  data: vehicle,
                })}
                <VSpacer medium />
              </>
            )}
          </InfoContainer>

          {vehicleCustomFieldDefinitions.length > 0 ? (
            <InfoContainer style={{ borderBottom: `1px solid #D4D4D4` }}>
              <Text variant="h6" style={{ cursor: `default`, fontWeight: 600 }} intlId="custom_fields.title" />
              <VSpacer medium />
              <CustomFields
                onUpdate={(customFields: CustomFieldsValueObject) => {
                  handleUpdate(`custom_fields`, customFields);
                }}
                customFields={vehicle.custom_fields}
                projectCustomFields={vehicleCustomFieldDefinitions}
              />
            </InfoContainer>
          ) : null}

          <VSpacer large />
        </div>
      );
    case 1:
      return (
        <div style={{ height: `100%`, overflowY: `auto`, minHeight: `300px` }}>
          <InfoContainer>
            <Text
              variant="h6"
              style={{ cursor: `default`, fontWeight: 600 }}
              intlId="clients.details.group.constraints"
            />

            <VSpacer medium />

            {userCredentials?.subscription.test(`planner.geofences`) &&
            userCredentials?.projects.test(project.id, `can_view_geofences`) &&
            project.geo_fences_enabled ? (
              <>
                <GeoFencesSelector
                  selectButtonStyles={{
                    padding: `3px 8px`,
                    borderRadius: `5px`,
                    marginBottom: `0px`,
                    paddingRight: `0px`,
                  }}
                  label={<Text intlId="geo_fences" variant="label" style={{ marginBottom: `8px` }} />}
                  geoFences={projectGeoFences ?? []}
                  selectedGeoFenceIds={vehicle.default_geo_fences}
                  handleUpdate={(geoFences: string[]) => {
                    handleUpdate(`default_geo_fences`, geoFences);
                  }}
                />
                <VSpacer medium />
              </>
            ) : null}

            {project.constraints.weight && (
              <>
                {renderInputText({
                  labelIntlId: `vehicles.data.details.default_max_weight`,
                  variant: `outlined`,
                  property: `default_max_weight`,
                  type: `number`,
                  handleUpdate: (property, value) => handleUpdate(property, SMToKm(value, project?.units?.weight)),
                  data: vehicle,
                  suffix: project?.units?.weight,
                })}
                <VSpacer medium />
              </>
            )}

            {project.constraints.volume && (
              <>
                {renderInputText({
                  labelIntlId: `vehicles.data.details.default_max_volume`,
                  variant: `outlined`,
                  property: `default_max_volume`,
                  type: `number`,
                  handleUpdate: (property, value) => handleUpdate(property, SMToKm(value, project?.units?.volume)),
                  data: vehicle,
                  suffix: project?.units?.volume,
                })}
                <VSpacer medium />
              </>
            )}

            {project.constraints.max_services && (
              <>
                {renderInputText({
                  labelIntlId: `vehicles.data.details.default_max_services`,
                  variant: `outlined`,
                  property: `default_max_services`,
                  type: `number`,
                  handleUpdate,
                  data: vehicle,
                })}
                <VSpacer medium />
              </>
            )}

            {project.constraints.max_distance && (
              <>
                {renderInputText({
                  labelIntlId: `vehicles.data.details.default_max_distance`,
                  variant: `outlined`,
                  property: `default_max_distance`,
                  type: `number`,
                  handleUpdate: (property, value) =>
                    handleUpdate(property, value ? SMToKm(value, project?.units?.distance) * 1000 : null),
                  data: vehicle,
                  suffix: project?.units?.distance,
                  computed: (value) => (value ? kmToSM(value / 1000, project?.units?.distance) : null),
                })}
                <VSpacer medium />
              </>
            )}

            {project.constraints.min_distance && (
              <>
                {renderInputText({
                  labelIntlId: `vehicles.data.details.default_min_distance`,
                  variant: `outlined`,
                  property: `default_min_distance`,
                  type: `number`,
                  handleUpdate: (property, value) =>
                    handleUpdate(property, value ? SMToKm(value, project?.units?.distance) * 1000 : null),
                  data: vehicle,
                  suffix: project?.units?.distance,
                  computed: (value) => (value ? kmToSM(value / 1000, project?.units?.distance) : null),
                })}
                <VSpacer medium />
              </>
            )}

            {project.constraints.max_time && (
              <>
                {renderInputText({
                  labelIntlId: `vehicles.data.details.default_max_time`,
                  variant: `outlined`,
                  property: `default_max_time`,
                  type: `number`,
                  handleUpdate: (property, value) => handleUpdate(property, value ? value * 60 : null),
                  data: vehicle,
                  suffix: `min`,
                  computed: (value) => (value ? value / 60 : null),
                })}
                <VSpacer medium />
              </>
            )}

            {project.constraints.min_time && (
              <>
                {renderInputText({
                  labelIntlId: `vehicles.data.details.default_min_time`,
                  variant: `outlined`,
                  property: `default_min_time`,
                  type: `number`,
                  handleUpdate: (property, value) => handleUpdate(property, value ? value * 60 : null),
                  data: vehicle,
                  suffix: `min`,
                  computed: (value) => (value ? value / 60 : null),
                })}
                <VSpacer medium />
              </>
            )}

            {renderTimewindows(`vehicles.data.details`, `default_time_window`, handleUpdate, vehicle, true)}
            <VSpacer medium />

            {project.constraints.provide_requires && (
              <>
                {renderArray(`vehicles.data.details`, `default_provides`, handleUpdate, vehicle)}
                <VSpacer medium />
              </>
            )}
          </InfoContainer>
          <VSpacer large />
        </div>
      );
    default:
      return null;
  }
};

interface VehicleProps {
  vehicle: Vehicle;
  activeProject: Project;
}

export const VehicleBody = ({ vehicle, activeProject }: VehicleProps) => {
  const { run: updateVehicle } = useUpdateVehicle();
  const { detailTab, setRightPanelInfo } = useRetrieveRightPanelInfo();

  const [infoSection, setInfoSection] = useState<number>(0);

  useEffect(() => {
    switch (detailTab) {
      case `constraints`:
        setInfoSection(1);
        break;
      default:
        setInfoSection(0);
        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab]);

  const handleUpdate = (label: string, val: any) => {
    updateVehicle({ vehicle, changes: { [label]: val } });
  };

  const handleUpdateDays: (val: WeekDay[]) => void = (val: WeekDay[]) => {
    const orderedDays = val.sort((a, b) => weekDays.indexOf(a) - weekDays.indexOf(b));
    updateVehicle({ vehicle, changes: { default_working_days: orderedDays } });
  };

  const formatWeakDays = () => {
    if (vehicle.default_working_days?.length === 7) {
      return intl.formatMessage({ id: `vehicles.working_days.every_day` });
    }
    return (vehicle.default_working_days ?? [])
      .map((day, index) => {
        return `${intl.formatMessage({ id: `${day}.short` })}${
          index < (vehicle.default_working_days ?? []).length - 1 ? `, ` : ``
        }`;
      })
      .join(``);
  };

  return (
    <Grid container direction="column" wrap="nowrap" style={{ height: `100%` }}>
      <InfoContainer>
        {renderInputText({
          placeholderIntlId: `vehicles.data.details.label`,
          variant: `clean`,
          property: `label`,
          size: `title`,
          handleUpdate,
          data: vehicle,
        })}

        <VSpacer medium />

        {activeProject?.planner.vehicleFieldsHasSpecificField(`phone`) && (
          <>
            <RenderInputPhone
              data={vehicle}
              defaultCountry={
                activeProject?.location?.country_code ? activeProject?.location?.country_code.toUpperCase() : undefined
              }
              property="phone"
              handleUpdate={handleUpdate}
              labelIntlId="vehicles.data.details.phone"
              placeholderIntlId="vehicles.data.details.phone"
            />
            <VSpacer medium />
          </>
        )}

        {activeProject?.planner.vehicleFieldsHasSpecificField(`external_id`) && (
          <>
            <Text
              intlId="vehicles.data.details.external_id"
              variant="label"
              style={{ cursor: `default`, marginBottom: `8px` }}
            />
            {renderInputText({
              placeholderIntlId: `vehicles.data.details.external_id`,
              variant: `outlined`,
              property: `external_id`,
              handleUpdate,
              data: vehicle,
            })}
            <VSpacer medium />
          </>
        )}

        <Text
          intlId="vehicles.data.details.labour_day"
          variant="label"
          style={{ cursor: `default`, marginBottom: `8px` }}
        />

        <RoutalSelect
          id={`working-days-popover`}
          containerStyle={{
            width: `100%`,
            height: `28px`,
            marginBottom: `8px`,
          }}
          justifyContent="space-between"
          popoverProps={{
            maxHeight: `200px`,

            options: weekDays.map((value: string, index: number): RoutalSelectMultiSelectOption => {
              return {
                key: `option-${index}`,
                listItemStyles: {
                  whiteSpace: `normal`,
                  overflow: `auto`,
                },
                closeOnClick: false,
                onClick: () => {
                  if (vehicle.default_working_days?.includes(value as WeekDay)) {
                    handleUpdateDays(vehicle.default_working_days?.filter((day) => day !== value));
                  } else {
                    handleUpdateDays([...(vehicle.default_working_days ?? []), value as WeekDay]);
                  }
                },
                content: (
                  <Grid style={{ display: `flex`, justifyContent: `space-between` }}>
                    <Text intlId={value} />
                    {vehicle.default_working_days?.includes(value as WeekDay) && (
                      <Check
                        style={{
                          color: RoutalPalette.success.medium,
                          fontSize: `24px`,
                        }}
                      />
                    )}
                  </Grid>
                ),
              };
            }),
          }}
        >
          <Text
            style={{
              cursor: `pointer`,
            }}
          >
            {formatWeakDays()}
          </Text>
        </RoutalSelect>

        <VSpacer medium />

        <InputLocations
          prefix="vehicles.data.details"
          propertyStart="default_start_location"
          propertyEnd="default_end_location"
          handleUpdate={(data: Record<string, any>) => {
            updateVehicle({ vehicle, changes: data });
          }}
          data={vehicle}
        />

        <VSpacer medium />
      </InfoContainer>

      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="center"
        style={{
          minHeight: `60px`,
          borderTop: `1px solid #CACACA`,
          borderBottom: `1px solid #CACACA`,
        }}
      >
        <RoutalTooltip key="vehicle-information" title={intl.formatMessage({ id: `properties.vehicle.information` })}>
          <InformationIcon
            isSelected={infoSection === 0}
            style={{
              cursor: infoSection === 0 ? `default` : `pointer`,
            }}
            onClick={() => {
              setRightPanelInfo({ detailTab: `info` });
            }}
          />
        </RoutalTooltip>

        <HSpacer large />

        <RoutalTooltip key="vehicle-constraints" title={intl.formatMessage({ id: `properties.vehicle.constraints` })}>
          <Constraints
            isSelected={infoSection === 1}
            style={{
              cursor: infoSection === 1 ? `default` : `pointer`,
            }}
            onClick={() => {
              setRightPanelInfo({ detailTab: `constraints` });
            }}
          />
        </RoutalTooltip>
      </Grid>

      <VehicleInfoSection vehicle={vehicle} section={infoSection} project={activeProject} handleUpdate={handleUpdate} />
    </Grid>
  );
};
